import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, GraduationCap, CheckCircle2, Briefcase, Euro, Accessibility, Target, BookOpen, Newspaper, ExternalLink, Award, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/Accordion';
import { FORMATIONS, LINKS } from '../data/constants';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
export function FormationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const formation = FORMATIONS.find(f => f.id === id);
  if (!formation) {
    return <Navigate to="/formations" replace data-id="element-1299" data-mp="1oaf5pt6pg2xc" />;
  }
  return <div className="flex flex-col w-full pb-24 bg-muted" data-id="element-1300" data-mp="1ta0awlcgx7ii">
      {/* Custom Header for Formation */}
      <div className="relative bg-lavande py-16 md:py-24 overflow-hidden" data-id="element-1301" data-mp="1fas9dlsu1nb5">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1302" data-mp="1fcv7kt452mqy" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1303" data-mp="272827xkzg69t" />

        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1304" data-mp="1y8bl4x6odk19">
          <div className="flex flex-wrap gap-3 mb-8" data-id="element-1305" data-mp="22aq2fcup4bqf">
            <Badge className="bg-primary text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1306" data-mp="22957hm4gyqs1">
              {formation.level}
            </Badge>
            <Badge variant="outline" className="bg-white/50 border-primary/20 text-primary-dark text-sm px-4 py-1.5 font-semibold" data-id="element-1307" data-mp="2ursnin4lzfm9">
              {formation.rncp}
            </Badge>
            <Badge variant="secondary" className="bg-brand-orange text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1308" data-mp="30mpfam180xrr">
              Session {formation.session}
            </Badge>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-primary-dark mb-6 tracking-tight leading-[1.1]" data-id="element-1309" data-mp="2qpkscjzjwy9w">
            {formation.title}
          </h1>

          <p className="text-xl md:text-2xl text-primary-dark/80 max-w-3xl leading-relaxed mb-10 font-medium" data-id="element-1310" data-mp="2gw3wlafut93l">
            {formation.description}
          </p>

          <div className="flex flex-wrap gap-8 text-base font-medium text-primary-dark bg-white p-8 rounded-2xl shadow-xl inline-flex border-none" data-id="element-1311" data-mp="27xiefgeanw3c">
            <div className="flex items-center gap-4 group" data-id="element-1312" data-mp="22x44fst4uc2o">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1313" data-mp="1xyj8i5kwyek1">
                <Clock className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1314" data-mp="1nwyp0w3mve2k" />
              </div>
              <div data-id="element-1315" data-mp="1sw1w0m36bud6">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1316" data-mp="1xt20wtoaer2p">
                  Durée
                </span>
                <span className="font-bold" data-id="element-1317" data-mp="22sobswgn7v8c">{formation.duration}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1318" data-mp="1555xl647q0wy"></div>
            <div className="flex items-center gap-4 group" data-id="element-1319" data-mp="1a3h7fguicbf6">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1320" data-mp="1a3zdf56z240a">
                <Calendar className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1321" data-mp="1jt0pikaec18z" />
              </div>
              <div data-id="element-1322" data-mp="me4wabfmdta8">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1323" data-mp="meoelt96mjos">
                  Rythme
                </span>
                <span className="font-bold" data-id="element-1324" data-mp="rbeykro3xzfh">{formation.rhythm}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1325" data-mp="2t2f2ly4j733g"></div>
            <div className="flex items-center gap-4 group" data-id="element-1326" data-mp="2o43srne8ksl8">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1327" data-mp="32rylh3s8uwyb">
                <GraduationCap className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1328" data-mp="2y0749juza3ku" />
              </div>
              <div data-id="element-1329" data-mp="3brbwi0w0ntl5">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1330" data-mp="3bqtv6puhyp6d">
                  Rentrée
                </span>
                <span className="font-bold" data-id="element-1331" data-mp="3lqt0mdvmdzf0">{formation.rentree}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-6xl mx-auto mt-16" data-id="element-1332" data-mp="3gt1b1qig6pdz">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1333" data-mp="1rcjqg5jlif5u">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-1334" data-mp="1remo9bqzpcl4">
            <Tabs defaultValue="programme" className="w-full" data-id="element-1335" data-mp="1mf0cz7uq26ey">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 h-auto p-1" data-id="element-1336" data-mp="1vy0kb33quq5">
                <TabsTrigger value="programme" className="py-3 text-sm md:text-base" data-id="element-1337" data-mp="6uhlq99vg2hg">
                  Programme
                </TabsTrigger>
                <TabsTrigger value="modalites" className="py-3 text-sm md:text-base" data-id="element-1338" data-mp="3tabzzup6859r">
                  Modalités
                </TabsTrigger>
                <TabsTrigger value="debouches" className="py-3 text-sm md:text-base" data-id="element-1339" data-mp="3k9np38v2ql13">
                  Débouchés
                </TabsTrigger>
                <TabsTrigger value="qualite" className="py-3 text-sm md:text-base" data-id="element-1340" data-mp="3abhp4wstvoyv">
                  Qualité
                </TabsTrigger>
              </TabsList>

              {/* TAB: Programme */}
              <TabsContent value="programme" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1341" data-mp="18jl14nph4mn7">
                <section data-id="element-1342" data-mp="1244e73izipoy">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1343" data-mp="122tat4tgbjyd">
                    <Target className="w-6 h-6 text-primary" data-id="element-1344" data-mp="x3zyvgttc10b" />
                    Objectifs pédagogiques mesurables
                  </h2>
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-100" data-id="element-1345" data-mp="t4zis62pzch0">
                    <p className="text-muted-foreground leading-relaxed" data-id="element-1346" data-mp="1psxaj13iqzk3">
                      {formation.objectives}
                    </p>
                  </div>
                </section>

                <section data-id="element-1347" data-mp="1g22q829u2a1a">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1348" data-mp="1g1ja8r3qtvpw">
                    <BookOpen className="w-6 h-6 text-primary" data-id="element-1349" data-mp="1bzxyxa6uxpht" />
                    Blocs de compétences
                  </h2>
                  <Accordion type="single" collapsible className="w-full" data-id="element-1350" data-mp="23tgik87zi5m9">
                    {formation.certification.blocs.map((bloc, i) => <AccordionItem key={i} value={`bloc-${i}`} data-id="element-1351" data-mp="3mhj55wwgnbt1" data-mpi={i}>
                        <AccordionTrigger className="text-left font-semibold hover:text-primary" data-id="element-1352" data-mp="3re9jaeopfxbt">
                          {bloc.code}
                        </AccordionTrigger>
                        <AccordionContent data-id="element-1353" data-mp="4a3g5th6efcx">
                          <p className="text-muted-foreground mt-2" data-id="element-1354" data-mp="4b4ty5pktfyc">
                            {bloc.description}
                          </p>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </section>
              </TabsContent>

              {/* TAB: Modalités */}
              <TabsContent value="modalites" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1355" data-mp="n1cnbw3h4s1c">
                <section className="space-y-6" data-id="element-1356" data-mp="s1qw5gcwg60f">
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1357" data-mp="wyhb20cywvk9">
                    <div data-id="element-1358" data-mp="wwo4sm69odcs">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1359" data-mp="7vc2jfa1pfz1">Public visé</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1360" data-mp="7xoenbt4gw46">
                        {formation.public}
                      </p>
                    </div>
                    <div data-id="element-1361" data-mp="cv7ugg5gxgy8">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1362" data-mp="cuokpmpz1yva">Prérequis</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1363" data-mp="3lai3y9botog1">
                        {formation.prerequisites}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1364" data-mp="3u3v33t0chmak">
                    <h3 className="text-lg font-bold mb-2" data-id="element-1365" data-mp="3u56b650tpg7h">
                      Méthodes mobilisées
                    </h3>
                    <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1366" data-mp="31misew9aud9k">
                      {formation.methods}
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1367" data-mp="2nhihr5ypy4ms">
                    <div data-id="element-1368" data-mp="2nfxodjo54rqi">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1369" data-mp="2s7yu0mqszk7k">
                        Évaluations en formation
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1370" data-mp="2dk42h9oj7lw2">
                        {formation.evaluation}
                      </p>
                    </div>
                    <div data-id="element-1371" data-mp="3691gh0lpmwj0">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1372" data-mp="365nehtgykpc3">
                        Épreuve certificative
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1373" data-mp="2wa287j4ux2wj">
                        {formation.epreuve}
                      </p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1374" data-mp="1ksu46yy4hosw">
                    <div data-id="element-1375" data-mp="1pu0015n2xs6n">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1376" data-mp="1pu9qsvfy83de">Accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1377" data-mp="2x4zp65nmy9os">
                        {formation.acces}
                      </p>
                    </div>
                    <div data-id="element-1378" data-mp="2nghrhxankipd">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1379" data-mp="2nf6nbwdap8xq">Délai d'accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1380" data-mp="2sdrfd8ie8mb3">
                        {formation.delaiAcces}
                      </p>
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* TAB: Débouchés */}
              <TabsContent value="debouches" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1381" data-mp="24ion0qkds0pv">
                <section data-id="element-1382" data-mp="2oz8qcqq0qsd">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1383" data-mp="2qscnk9y8wwo">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1384" data-mp="7nah0j1dat8h" />
                    Débouchés officiels
                  </h2>
                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-1385" data-mp="1s4jn61madmpt">
                    {formation.opportunities.map((job, i) => <Badge key={i} variant="secondary" className="text-sm px-4 py-2" data-id="element-1386" data-mp="21w5u3lrlczmr" data-mpi={i}>
                        {job}
                      </Badge>)}
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6" data-id="element-1387" data-mp="26virv1k03r44">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1388" data-mp="26xv5j2iq7fbd">
                      Codes ROME correspondants :
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1389" data-mp="2fx8k29lpiu2y">
                      {formation.romeCodes.map((code, i) => <Badge key={i} variant="outline" className="bg-white" data-id="element-1390" data-mp="qhsipge0ycnm" data-mpi={i}>
                          {code}
                        </Badge>)}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1391" data-mp="2lmi9rqtbhe8a">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1392" data-mp="2qjs49kvkcg2z">
                      Secteurs :
                    </span>
                    <p className="text-muted-foreground" data-id="element-1393" data-mp="2zk6pkk8j1v6h">
                      {formation.secteurs}
                    </p>
                  </div>
                </section>

                <section data-id="element-1394" data-mp="34h70b8gbnm3x">
                  <h2 className="text-2xl font-bold font-heading mb-4 flex items-center gap-2" data-id="element-1395" data-mp="39i4g5edv0429">
                    <GraduationCap className="w-6 h-6 text-primary" data-id="element-1396" data-mp="3e931isdpfscd" />
                    Poursuites d'études
                  </h2>
                  <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1397" data-mp="3i9mtlhjf85rn">
                    {formation.furtherStudies}
                  </p>
                </section>
              </TabsContent>

              {/* TAB: Qualité */}
              <TabsContent value="qualite" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1398" data-mp="67es972850lw">
                <section data-id="element-1399" data-mp="17jxutj8psw3">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1400" data-mp="1aem6w5djxus">
                    Indicateurs de résultats
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1401" data-mp="3odjar2woib77">
                    {formation.indicators.map((ind, i) => <Card key={i} className="bg-primary/5 border-none" data-id="element-1402" data-mp="owl367ws0nx5" data-mpi={i}>
                        <CardContent className="p-6 text-center flex flex-col justify-center h-full" data-id="element-1403" data-mp="jyb9k1qp9vdn">
                          <div className="text-3xl font-bold text-primary mb-2 font-heading" data-id="element-1404" data-mp="f7bb2s1xn591">
                            {ind.label}
                          </div>
                          <div className="text-sm font-medium text-foreground" data-id="element-1405" data-mp="17odve040u53t">
                            {ind.value}
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                </section>

                <section data-id="element-1406" data-mp="nhqpxtluk3mh">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1407" data-mp="sehaat4opldp">
                    Repères RNCP
                  </h2>
                  <Card className="border-none bg-slate-50" data-id="element-1408" data-mp="eg118vjcnise">
                    <CardContent className="p-6 space-y-4" data-id="element-1409" data-mp="igkuvp4ps49s">
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1410" data-mp="iguqbs8j33mv">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1411" data-mp="166xf9zh5bth9">
                          Intitulé exact
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1412" data-mp="xeddwr9gzasq">
                          {formation.certification.intitule}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1413" data-mp="1p59p7nfvt7dw">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1414" data-mp="1tvhozdfr9jo0">
                          Niveau / Domaine
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1415" data-mp="3ekkf7t61w47r">
                          {formation.certification.niveauDomaine}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1416" data-mp="3oan7ts99fg5k">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1417" data-mp="3je6g6o2uqjnq">
                          Certificateur
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1418" data-mp="2qog0wbphhbq1">
                          {formation.certification.certificateur}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1419" data-mp="30mdn6tns8yg0">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1420" data-mp="kv7xepwyz4jw">
                          Enregistrement
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1421" data-mp="fvd82qsuen0y">
                          {formation.certification.enregistrement}
                        </div>
                      </div>
                      {formation.certification.derniereDelivrance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1422" data-mp="wwzidkqusgjv">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1423" data-mp="11x5f3fbzct8t">
                            Dernière délivrance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1424" data-mp="46qz1pkkzq3p">
                            {formation.certification.derniereDelivrance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1425" data-mp="ixhq2vecml5r">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1426" data-mp="3hnzxhnlsmryx">
                          Capitalisation
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1427" data-mp="3ml9txn7lo3w9">
                          {formation.certification.capitalisation}
                        </div>
                      </div>
                      {formation.certification.correspondance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1428" data-mp="1uq8wih8fghlg">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1429" data-mp="1psqkghz45m98">
                            Correspondance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1430" data-mp="1kup2y1hc5uzv">
                            {formation.certification.correspondance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4" data-id="element-1431" data-mp="171ppe5146auy">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1432" data-mp="16zcxp05k0m4p">
                          Lien officiel
                        </div>
                        <div className="sm:col-span-2 font-medium break-all" data-id="element-1433" data-mp="x30565lqwebj">
                          <a href={`https://${formation.certification.lien}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-id="element-1434" data-mp="2lxqpypblzzsl">
                            {formation.certification.lien}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </TabsContent>
            </Tabs>

            {/* Points forts de la formation */}
            <Card className="border-none shadow-lg bg-white overflow-hidden" data-id="element-1435" data-mp="1t9utnlpyc9wc">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1436" data-mp="1y7d5bjvct381" />
              <CardHeader className="pt-8" data-id="element-1437" data-mp="1y5s9zs17tg94">
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark flex items-center gap-2" data-id="element-1438" data-mp="1ja40poh1i2yd">
                  <Target className="w-6 h-6 text-brand-orange" data-id="element-1439" data-mp="1o8noluf71otd" />
                  Les métiers visés
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 pt-2" data-id="element-1440" data-mp="10mbmj3gh7zsc">
                <p className="text-muted-foreground leading-relaxed mb-6" data-id="element-1441" data-mp="10nmyvrnzmfv2">
                  À l'issue de cette formation, vous pourrez exercer sur de
                  nombreux postes recherchés par les entreprises :
                </p>
                <div className="grid sm:grid-cols-2 gap-3" data-id="element-1442" data-mp="19pcpwwdyjcsb">
                  {formation.opportunities.map((job, i) => <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-1443" data-mp="1jlfusa071piy" data-mpi={i}>
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0" data-id="element-1444" data-mp="1sdhntn7xo3j7" />
                      <span className="font-medium text-primary-dark text-sm" data-id="element-1445" data-mp="1sfkmswr2d702">
                        {job}
                      </span>
                    </div>)}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-1446" data-mp="229ugqivnmdcw">
                  <Button className="w-full group bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-md" asChild data-id="element-1447" data-mp="2esuccr088j82">
                    <Link to="/candidater" data-id="element-1448" data-mp="2ntsjhi5yp6uv">
                      Je postule à cette formation
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1449" data-mp="2jutgw47pdm6l" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* CAP 360 — Accompagnement recherche d'alternance */}
            <Cap360 data-id="element-1450" data-mp="24x9cozimrv1h" />
          </div>

          {/* Sidebar / Sticky Actions */}
          <div className="lg:col-span-1" data-id="element-1451" data-mp="205998x3cqk05">
            <div className="sticky top-28 space-y-8" data-id="element-1452" data-mp="2047yyuu5u1jh">
              <Card className="border-none shadow-xl bg-white overflow-hidden" data-id="element-1453" data-mp="1cf560qu2sm9o">
                <div className="h-2 w-full bg-gradient-to-r from-primary to-brand-orange" data-id="element-1454" data-mp="18el8vn9i5inj" />
                <CardHeader className="bg-white pb-6 pt-8" data-id="element-1455" data-mp="yaeogwzu70wt">
                  <CardTitle className="text-2xl font-extrabold font-heading text-center text-primary-dark" data-id="element-1456" data-mp="y8tub8hfpjzh">
                    Intéressé(e) par cette formation ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-0 space-y-4" data-id="element-1457" data-mp="oadzaufadqkp">
                  <Button className="w-full text-lg py-7 group bg-brand-orange hover:bg-[#E67E22] text-white shadow-md" asChild data-id="element-1458" data-mp="tc3cafm2kebf">
                    <Link to="/candidater" data-id="element-1459" data-mp="1lyxtn3nlaxko">
                      Je candidate maintenant
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1460" data-mp="173hu4j48q9gw" />
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full py-6 border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-1461" data-mp="1c3mfamg3fsdg">
                    <Link to="/qualite#indicateurs" data-id="element-1462" data-mp="239tcanovil9u">
                      <Target className="w-5 h-5 mr-2" data-id="element-1463" data-mp="238ibmwqq5tmy" />
                      Voir nos indicateurs de résultats
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white" data-id="element-1464" data-mp="1th5htzld8tea">
                <CardContent className="p-8 space-y-8" data-id="element-1465" data-mp="1ojvuqqlg8ttm">
                  <div className="group" data-id="element-1466" data-mp="1jl2ckkv7wen4">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1467" data-mp="1jmvp2gsiht31">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1468" data-mp="1fifiedb2qhmr">
                        <Euro className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1469" data-mp="15m2q9jv6gbu4" />
                      </div>
                      Tarif et financement
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13" data-id="element-1470" data-mp="2zzkg5dj21ksb">
                      {formation.price}
                    </p>
                  </div>

                  <div className="pt-8 border-t border-slate-100 group" data-id="element-1471" data-mp="37ithqklbkf6t">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1472" data-mp="3cgvajx8jrsvh">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1473" data-mp="3h9enqt3bolzr">
                        <Accessibility className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1474" data-mp="2okh4onrael5y" />
                      </div>
                      Accessibilité handicap
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13 mb-4" data-id="element-1475" data-mp="2yietbcd26jz3">
                      {formation.handicap}
                    </p>
                    <div className="pl-13" data-id="element-1476" data-mp="33j2toon6gd5z">
                      <Button variant="outline" size="sm" className="border-primary/20 text-primary-dark hover:bg-primary/5" asChild data-id="element-1477" data-mp="ds672ckbzs6m">
                        <Link to="/accessibilite" data-id="element-1478" data-mp="gd47dxad9kei">
                          En savoir plus
                          <ArrowRight className="w-4 h-4 ml-2" data-id="element-1479" data-mp="ccbvsxe0v3f2" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Réussite & Centre d'examen — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-muted" data-id="element-1480" data-mp="v93ep5pnjgil">
          <div className="container max-w-5xl mx-auto space-y-10" data-id="element-1481" data-mp="1919swm98vdfe">
            {/* 100% de réussite */}
            <div className="bg-primary-dark text-white rounded-3xl shadow-xl overflow-hidden relative" data-id="element-1482" data-mp="1426kqsxywr37">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1483" data-mp="1iz7fz6fdb46f" />
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-10 md:p-12" data-id="element-1484" data-mp="1dyjhy0sq1n2p">
                <div className="shrink-0 text-center" data-id="element-1485" data-mp="21nrc3zglv6k9">
                  <div className="text-6xl md:text-7xl font-extrabold font-heading text-brand-orange leading-none" data-id="element-1486" data-mp="21n7u6iqygi68">
                    100%
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-3 text-lavande font-bold uppercase tracking-wider text-sm" data-id="element-1487" data-mp="1smjpiengbu60">
                    <Award className="w-5 h-5" data-id="element-1488" data-mp="1dwm0jwhp70fr" />
                    de réussite
                  </div>
                </div>
                <div className="w-px h-24 bg-white/10 hidden md:block" data-id="element-1489" data-mp="1iqq74en1x6gb" />
                <div className="text-center md:text-left" data-id="element-1490" data-mp="14z1xqj46sw31">
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-3" data-id="element-1491" data-mp="19w230rt7put3">
                    Session d'examen 2025 : 100 % de réussite
                  </h2>
                  <p className="text-lavande text-lg leading-relaxed" data-id="element-1492" data-mp="zye6bwc1qd8a">
                    L'intégralité de nos candidats au TP Conseiller de Vente a
                    obtenu sa certification lors de la session d'examen 2025. Un
                    résultat qui récompense l'accompagnement individualisé et la
                    pédagogie par l'action de Zillenium Alternance.
                  </p>
                </div>
              </div>
            </div>

            {/* Centre d'examen agréé */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden" data-id="element-1493" data-mp="zsmxu1su6y6h">
              <div className="h-2 w-full bg-primary" data-id="element-1494" data-mp="utk3bbahgacr" />
              <div className="p-8 md:p-12" data-id="element-1495" data-mp="uu3d24pzbsfp">
                <div className="flex items-start gap-5 mb-6" data-id="element-1496" data-mp="puh1dzpsuk90">
                  <div className="w-14 h-14 rounded-2xl bg-lavande flex items-center justify-center shrink-0" data-id="element-1497" data-mp="psmdccj8ec0p">
                    <ShieldCheck className="w-7 h-7 text-primary" data-id="element-1498" data-mp="kxgrfdptwxcm" />
                  </div>
                  <div data-id="element-1499" data-mp="3alzhtdm9e02u">
                    <span className="inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-2" data-id="element-1500" data-mp="35llci3c1l48i">
                      Centre d'examen agréé
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1501" data-mp="1q248e7lhfd93">
                      Zillenium, centre d'examen du titre professionnel
                    </h2>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-1502" data-mp="1ztrsfnfpls0j">
                  Conformément à l'article L. 335-6 du code de l'éducation, le
                  titre professionnel est une certification délivrée au nom du
                  ministre chargé de l'emploi qui atteste de compétences
                  directement opérationnelles en entreprise. La délivrance de
                  cette certification s'appuie sur le respect des modalités
                  d'examen fixées réglementairement. Dans ce cadre, Zillenium
                  veille à ce que le responsable de session ainsi que les
                  membres de jury habilités bénéficient d'une information
                  complète et actualisée des modalités d'examen décrites dans le
                  référentiel de certification.
                </p>

                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6" data-id="element-1503" data-mp="300dpxva0y6l0">
                  <h3 className="font-bold text-primary-dark mb-2" data-id="element-1504" data-mp="302humffqz9mc">
                    Décision de renouvellement d'agrément n° 0350 — 7 mai 2026
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-1505" data-mp="27chsgocmsan6">
                    Agrément initialement délivré le 13 mai 2025, renouvelé par
                    cette décision pour la période en cours.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-1506" data-mp="2cb2k3zdthm00">
                    <div data-id="element-1507" data-mp="2cd5mliw5p7lm">
                      <span className="text-muted-foreground block mb-1" data-id="element-1508" data-mp="3sp2hb2la2xjk">
                        Autorité
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1509" data-mp="3np6gtdbsho8r">
                        Préfète de la région Bourgogne-Franche-Comté (DREETS)
                      </span>
                    </div>
                    <div data-id="element-1510" data-mp="448vdz0npx9x">
                      <span className="text-muted-foreground block mb-1" data-id="element-1511" data-mp="41cvi5590yiu">
                        Validité totale de l'agrément
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1512" data-mp="3q9w332tyu53v">
                        Du 13 mai 2025 au 12 mai 2027
                      </span>
                    </div>
                    <div data-id="element-1513" data-mp="mtnrrg5edy7z">
                      <span className="text-muted-foreground block mb-1" data-id="element-1514" data-mp="msvy4avd2akc">
                        Lieu des sessions d'examen
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1515" data-mp="d44bmi92kigt">
                        25 rue Gabriel Cortel, 89300 Joigny
                      </span>
                    </div>
                    <div data-id="element-1516" data-mp="10rlxxy9keoce">
                      <span className="text-muted-foreground block mb-1" data-id="element-1517" data-mp="15k6o8pt9ijb6">
                        Capacité par session
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1518" data-mp="270xb30cv605d">
                        Jusqu'à 30 candidats
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>}

      {/* Dans la presse — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-white" data-id="element-1519" data-mp="1o45k04r2ygqr">
          <div className="container max-w-4xl mx-auto" data-id="element-1520" data-mp="1s3wikumum15u">
            <div className="text-center mb-10" data-id="element-1521" data-mp="1s4oc7zwvxoth">
              <div className="inline-flex items-center justify-center p-3 bg-lavande rounded-2xl mb-4" data-id="element-1522" data-mp="1x3iyxcxz3xiv">
                <Newspaper className="w-7 h-7 text-primary" data-id="element-1523" data-mp="222vj1pxgmqhr" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1524" data-mp="38iek7hr4kx8e">
                Dans la presse
              </h2>
            </div>
            <a href="https://www.lyonne.fr/joigny-89300/actualites/il-y-a-un-manque-sur-joigny-des-etudiants-mobilises-pour-sensibiliser-au-don-du-sang-dans-la-galerie-marchande_14961315/" target="_blank" rel="noopener noreferrer" className="group block bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-1525" data-mp="3dfo7aqr1kwt2">
              <div className="flex flex-col sm:flex-row" data-id="element-1526" data-mp="e4bjlopl5swt">
                <div className="sm:w-48 shrink-0 bg-primary-dark flex flex-col items-center justify-center p-8 text-center" data-id="element-1527" data-mp="e6xqdm4nk4dz">
                  <Newspaper className="w-10 h-10 text-lavande mb-3" data-id="element-1528" data-mp="46nbzy6vsqx8" />
                  <span className="text-white font-bold font-heading leading-tight" data-id="element-1529" data-mp="48hvd82i8az7">
                    L'Yonne Républicaine
                  </span>
                  <span className="text-lavande text-sm mt-1" data-id="element-1530" data-mp="1ylhkbrsryhj5">lyonne.fr</span>
                </div>
                <div className="p-8 flex-1" data-id="element-1531" data-mp="3szlo7r2577dp">
                  <span className="inline-block bg-brand-orange/10 text-brand-orange text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" data-id="element-1532" data-mp="3syk2msni32hq">
                    Engagement étudiant • Joigny
                  </span>
                  <h3 className="text-xl font-extrabold font-heading text-primary-dark mb-3 leading-snug group-hover:text-primary transition-colors" data-id="element-1533" data-mp="3o10m1m3bydmm">
                    « Il y a un manque sur Joigny » : des étudiants mobilisés
                    pour sensibiliser au don du sang dans la galerie marchande
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4" data-id="element-1534" data-mp="6kacae00fkeb">
                    Nos alternants s'engagent sur le terrain au service d'une
                    cause solidaire — une illustration concrète de la pédagogie
                    par l'action chez Zillenium Alternance.
                  </p>
                  <span className="inline-flex items-center text-primary font-bold group-hover:gap-2 transition-all" data-id="element-1535" data-mp="2ve7i6uun8y8e">
                    Lire l'article
                    <ExternalLink className="w-4 h-4 ml-1" data-id="element-1536" data-mp="2iuse1ckkmwx1" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>}

      {/* Témoignages d'alternants — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <>
          <div className="mt-24" data-id="element-1537" data-mp="2522rn714rpze">
            <SocialProof title="On les accompagne dans leurs projets de marque" subtitle="Au-delà du diplôme, nos conseillers de vente créent, lancent et défendent de vrais projets de création de marque." testimonials={[{
          url: 'https://www.instagram.com/reel/DXKberwjoL6/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }, {
          url: 'https://www.instagram.com/p/DXNAq0yjk7y/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }]} featuredIndex={0} data-id="element-1538" data-mp="2535ft772jebm" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1539" data-mp="tkhzxdukukby">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1540" data-mp="yfe1p5pm8m29" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1541" data-mp="189xl8ebc9xk9">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1542" data-mp="1ce5dglh09vn8">
                Envie de vivre l'aventure ?
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1543" data-mp="3e4p5drtf8hsp">
                Rejoignez le TP Conseiller de Vente et donnez vie à vos projets
                commerciaux dès la rentrée.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1544" data-mp="3bl2hswet77o2">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1545" data-mp="36s9g6hen0fg7">
                  <Link to="/candidater" data-id="element-1546" data-mp="2wwpjvtw5veqx">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1547" data-mp="3uhkbvkh4rjnh" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1548" data-mp="3kkfjh2woyry8">
                  <Link to="/entreprises" data-id="element-1549" data-mp="2937gmm1p1jw6">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}

      {/* Témoignages d'alternants — BTS NDRC uniquement */}
      {formation.id === 'bts-ndrc' && <>
          <div className="mt-24" data-id="element-1550" data-mp="2bjfkpweol3dn">
            <SocialProof data-id="element-1551" data-mp="1wn6ol3q3765g" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1552" data-mp="1wpaolakv7l0g">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1553" data-mp="21o42v57z7g1o" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1554" data-mp="1smwb75zytdgk">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1555" data-mp="1dxrnj9aeih12">
                Convaincu(e) ? Rejoignez la prochaine promotion
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1556" data-mp="14viigyxwjfv2">
                Lancez votre candidature dès aujourd'hui et construisez votre
                avenir dans la relation client.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1557" data-mp="qb5xf0a78uwz">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1558" data-mp="lbta6eff1ltv">
                  <Link to="/candidater" data-id="element-1559" data-mp="197xp9y3t4icn">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1560" data-mp="195tecw7hqj2v" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1561" data-mp="1hz6b698oe4u8">
                  <Link to="/entreprises" data-id="element-1562" data-mp="1mzudhr2cu83r">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}
    </div>;
}